<template>
  <component
    :is="dialog ? 'v-dialog' : 'v-menu'"
    max-width="590"
    offset-y
    v-model="open"
    :close-on-content-click="false"
    allow-overflow
  >
    <template v-slot:activator="{ on }">
      <div
        v-on="on"
        class="date-range-picker-input-wrapper"
        style="cursor: pointer;"
      >
        <v-text-field
          :value="formattedValue"
          :dark="dark"
          :light="light"
          hide-details
          single-line
          :placeholder="placeholder"
          :outlined="outlined"
          append-icon="fa-calendar"
          readonly
        ></v-text-field>
      </div>
    </template>
    <div class="date-range-picker-content-wrapper pa-2" :class="{small, large: !small}">
      <div
        v-if="range === 'last10loads'"
        class="text-center pt-4 pb-4"
      >
        <div>Displaying last 10 loads</div>
        <v-btn
          color="primary"
          @click="range = null"
        >
          Switch to Date Range
        </v-btn>
      </div>
      <v-layout
        v-else
        row
        wrap
        class="mb-3"
      >
        <v-flex xs12 md6>
          <v-date-picker
            :value="startMoment ? startMoment.format('YYYY-MM-DD') : null"
            color="primary lighten-1"
            header-color="primary"
            no-title
            :max="endMoment ? endMoment.format('YYYY-MM-DD') : null"
            @input="update('date', 'start', $event)"
          ></v-date-picker>
          <div class="text-center mb-1">
            <TimePicker
              style="display: inline-block"
              :value="startMoment ? startMoment.format('HH:mm:ss') : null"
              :disabled="!startMoment"
              @input="update('time', 'start', $event)"
            />
          </div>
        </v-flex>
        <v-flex xs12 md6>
          <v-date-picker
            :value="endMoment ? endMoment.format('YYYY-MM-DD') : null"
            color="primary lighten-1"
            header-color="primary"
            no-title
            :min="startMoment ? startMoment.format('YYYY-MM-DD') : null"
            @input="update('date', 'end', $event)"
          ></v-date-picker>
          <div class="text-center mb-1">
            <TimePicker
              style="display: inline-block"
              :value="endMoment ? endMoment.format('HH:mm:ss') : null"
              :disabled="!endMoment"
              @input="update('time', 'end', $event)"
            />
          </div>
        </v-flex>
      </v-layout>
      <v-divider />
      <div class="text-xs-left" v-if="range !== 'last10loads'">
        <v-select
          class="pt-0"
          :items="visibleCommonRanges"
          v-model="commonRangeSelection"
          @input="selectCommonFromDropdown"
          placeholder="Common Ranges"
          hide-details
          style="max-width: 150px; display: inline-block;"
        />
        <v-btn
          v-for="(range, $index) in commonRangeButtons"
          :key="$index"
          color="default"
          text
          @click="selectCommonFromButton(range)"
        >{{range.button}}
        </v-btn>
        <v-btn
          style="float: right"
          color="primary"
          @click="apply"
        >
          Apply
        </v-btn>
      </div>
    </div>
  </component>
</template>

<style lang="scss">
.date-range-picker-input-wrapper * {
  cursor: pointer;
}
.date-range-picker-content-wrapper {
  background: #fff;
  &.large {
    max-height: 85vh;
  }
  &.small {
    text-align: center;
  }
  .v-date-picker-table {
    height: 220px;
  }
}
</style>

<script>
import get from 'lodash/get'
import moment from 'moment-timezone'
import TimePicker from './TimePicker'
import toNumber from 'lodash/toNumber'
import { VDialog, VMenu } from 'vuetify/lib'

let commonRanges = [
  {
    name: 'Last 8 Hours',
    button: 'Last 8 Hours',
    fn: (t = moment()) => ({ start: t.clone().subtract(8, 'hours').toISOString(), end: t.clone().toISOString(), range: null })
  },
  {
    name: 'Last 10 Loads',
    button: 'Last 10 Loads',
    fn: () => ({ range: 'last10loads' }),
    show: (vm) => !!vm.allowLast10
  },
  {
    name: 'Last 7 Days',
    fn: (t = moment()) => ({ start: t.clone().subtract(7, 'days').toISOString(), end: t.clone().toISOString(), range: null })
  },
  {
    name: 'Last 30 Days',
    fn: (t = moment()) => ({ start: t.clone().subtract(30, 'days').toISOString(), end: t.clone().toISOString(), range: null })
  },
  {
    name: 'Last 90 Days',
    fn: (t = moment()) => ({ start: t.clone().subtract(90, 'days').toISOString(), end: t.clone().toISOString(), range: null })
  },
  {
    name: 'Last Year',
    fn: (t = moment()) => ({ start: t.clone().subtract(1, 'years').toISOString(), end: t.clone().toISOString(), range: null })
  }
]

export default {
  components: {
    TimePicker,
    VDialog,
    VMenu
  },
  props: {
    dateFormat: {
      type: String,
      default: 'YYYY-MM-DD h:mm a'
    },
    placeholder: {
      type: String,
      default: 'Select Date Range'
    },
    timezone: {
      type: String,
      default: () => moment.tz.guess()
    },
    allowLast10: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    light: {
      type: Boolean,
      default: false
    },
    dialog: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      default: () => ({ start: null, end: null })
    }
  },
  data () {
    return {
      open: false,
      start: null,
      end: null,
      range: null,
      commonRangeSelection: null
    }
  },
  computed: {
    formattedValue () {
      let start = this.start
      let end = this.end
      let range = this.range
      if (!this.open) {
        start = get(this.value, 'start')
        end = get(this.value, 'end')
        if (this.allowLast10) {
          range = get(this.value, 'range')
        }
      }

      if (range) {
        if (range === 'last10loads') {
          return 'Last 10 Loads'
        }
        return range
      }

      if (!start && !end) {
        return ''
      }
      return `${start ? moment(start).tz(this.timezone).format(this.dateFormat) : '?'} -- ${end ? moment(end).tz(this.timezone).format(this.dateFormat) : '?'}`
    },
    small () {
      return this.$vuetify.breakpoint.smAndDown
    },
    startMoment () {
      if (!this.start) {
        return null
      }
      return moment(this.start).tz(this.timezone)
    },
    endMoment () {
      if (!this.end) {
        return null
      }
      return moment(this.end).tz(this.timezone)
    },
    commonRanges () {
      return commonRanges.filter(r => typeof r.show !== 'function' || r.show(this))
    },
    visibleCommonRanges () {
      let _commonRanges = this.commonRanges
      if (!this.small) {
        _commonRanges = this.commonRanges.filter(v => !v.button)
      }
      return _commonRanges.map((v, i) => ({
        ...v,
        text: v.name,
        value: i
      }))
    },
    commonRangeButtons () {
      if (this.small) {
        return []
      }
      return this.commonRanges.filter(v => !!v.button)
    }
  },
  watch: {
    value () {
      this.readValues()
    },
    open () {
      this.readValues()
    }
  },
  methods: {
    readValues () {
      this.start = get(this.value, 'start')
      if (this.start && typeof this.start === 'object') {
        this.start = this.start.toISOString()
      }
      this.end = get(this.value, 'end')
      if (this.end && typeof this.end === 'object') {
        this.end = this.end.toISOString()
      }
      if (this.allowLast10) {
        this.range = get(this.value, 'range')
      }
    },
    update (type, key, value) {
      let methods = {
        date: ['year', 'month', 'date'],
        time: ['hour', 'minute']
      }[type]
      let m = this[key + 'Moment'] ? this[key + 'Moment'].clone() : moment.tz(value + (type === 'date' ? 'T12:00:00' : ''), this.timezone)
      value.split({ date: '-', time: ':' }[type]).map((v, index) => {
        v = toNumber(v)
        if (methods[index] && (v || v === 0)) {
          if (methods[index] === 'month') {
            v-- // Since months are 0 - 11
          }
          m[methods[index]](v)
        }
      })
      this[key] = m.toISOString()
      this.commonRangeSelection = null
    },
    apply () {
      if ((!this.start || !this.end) && !this.range) {
        return
      }
      this.$emit('input', { start: this.start, end: this.end, range: this.range })
      this.open = false
    },
    async selectCommonFromDropdown (index) {
      this.selectCommon(this.visibleCommonRanges[index])
      await this.$nextTick()
    },
    selectCommonFromButton (range) {
      this.selectCommon(range)
      this.apply()
    },
    selectCommon (range) {
      console.log('select common', range)
      Object.entries(range.fn()).forEach(([key, value]) => { this[key] = value })
    }
  }
}
</script>
